import { isNotNil } from 'es-toolkit'
import Fuse from 'fuse.js'
import type { Position } from 'geojson'
import { useCallback, useMemo } from 'react'
import type { AnyAlert } from '../../data/alerts/types.ts'
import { useAlerts } from '../../data/alerts/useAlerts.ts'
import { severityAsNumberMap } from '../../data/alerts/utils.ts'
import { useFavoriteAssets } from '../../data/assets.ts'
import { isAssetLocation, type Location, useLocations } from '../../data/locations.tsx'
import { useMeasurementAlertsRecord } from '../../data/measurementAlerts.ts'
import type { AnyProtection } from '../../data/protections/types.ts'
import { useProtections } from '../../data/protections/useProtections.ts'
import { protectionTypePriorityOrder } from '../../data/protections/utils.ts'
import { type LocationFilter, useLocationFilter } from '../../states.ts'

export const searchLocations = <T extends Location>(locations: T[], search: string) => {
	if (!search) return locations
	return new Fuse(locations, { keys: ['name', 'type', 'id'], threshold: 0.4 }).search(search).map((result) => result.item)
}

export const useSearchLocations = <T extends Location>(locations: T[], search: string) =>
	useMemo(() => searchLocations(locations, search), [locations, search])

const filterLocationsByFavorites = (locations: Location[], favorites: ReturnType<typeof useFavoriteAssets>['data']) =>
	locations.filter((loc) => isAssetLocation(loc) && favorites?.some((fav) => fav.assetVrn === loc.assetVrn))

const filterLocationsByAlerting = (
	locations: Location[],
	alerts: ReturnType<typeof useAlerts>['alerts'],
	assetProtections: ReturnType<typeof useProtections>['assetProtections'],
	measurementAlerts: ReturnType<typeof useMeasurementAlertsRecord>,
) =>
	locations.filter((loc) => {
		const protections = loc.type === 'asset' ? (assetProtections[loc.assetVrn] ?? []) : []
		const ma = loc.type === 'device' ? measurementAlerts[loc.id] : undefined
		const activeAlerts = protections.map((p) => alerts[p.vrn]).filter(isNotNil)
		return isInAlert(activeAlerts, ma)
	})

const filterLocationsByNoAlertSet = (
	locations: Location[],
	assetProtections: ReturnType<typeof useProtections>['assetProtections'],
	measurementAlerts: ReturnType<typeof useMeasurementAlertsRecord>,
) =>
	locations.filter(
		(loc) =>
			!(loc.type === 'device' && (measurementAlerts[loc.id]?.active.length || measurementAlerts[loc.id]?.inactive.length)) &&
			!(loc.type === 'asset' && assetProtections[loc.assetVrn]?.length),
	)

export const useFilteredLocations = () => {
	const { isPending: isLocationsPending } = useLocations()
	const { isPending: isFavoriteAssetsPending } = useFavoriteAssets()
	const { isPending: isAlertsPending } = useAlerts()
	const { isPending: isProtectionsPending } = useProtections()
	const filter = useLocationFilter((s) => s.filter)
	const getLocationsForFilter = useGetLocationsForFilter()

	return {
		locations: useMemo(() => getLocationsForFilter(filter ?? 'all'), [getLocationsForFilter, filter]),
		isPending: isLocationsPending || isFavoriteAssetsPending || isAlertsPending || isProtectionsPending,
	}
}

export const useLocationCategoryCounts = () => {
	const { data: locations } = useLocations()
	const { data: favorites } = useFavoriteAssets()
	const { alerts } = useAlerts()
	const { assetProtections } = useProtections()
	const measurementAlerts = useMeasurementAlertsRecord()

	return useMemo(() => {
		return {
			all: locations.length,
			favorites: filterLocationsByFavorites(locations, favorites).length,
			alerting: filterLocationsByAlerting(locations, alerts, assetProtections, measurementAlerts).length,
			noAlertsSet: filterLocationsByNoAlertSet(locations, assetProtections, measurementAlerts).length,
		}
	}, [locations, favorites, alerts, assetProtections, measurementAlerts])
}

export const useGetLocationsForFilter = () => {
	const { data: allLocations } = useLocations()
	const { data: favorites } = useFavoriteAssets()
	const { alerts } = useAlerts()
	const { assetProtections } = useProtections()
	const measurementAlerts = useMeasurementAlertsRecord()

	return useCallback(
		(filter: LocationFilter) => {
			if (filter === 'favorites') return filterLocationsByFavorites(allLocations, favorites)
			if (filter === 'alerting') return filterLocationsByAlerting(allLocations, alerts, assetProtections, measurementAlerts)
			if (filter === 'no-alerts-set') return filterLocationsByNoAlertSet(allLocations, assetProtections, measurementAlerts)
			return allLocations
		},
		[allLocations, favorites, alerts, assetProtections, measurementAlerts],
	)
}

export const useIsFavoriteAsset = (loc: Location) => {
	const { data: favorites } = useFavoriteAssets()
	return isAssetLocation(loc) ? favorites?.some((fav) => fav.assetVrn === loc.assetVrn) : false
}

type MeasurementAlert = ReturnType<typeof useMeasurementAlertsRecord>[string] | undefined

const getLocationProtections = (loc: Location, assetProtections: ReturnType<typeof useProtections>['assetProtections']) =>
	loc.type === 'asset' ? (assetProtections[loc.assetVrn] ?? []) : []

const getLocationMeasurementAlerts = (loc: Location, measurementAlerts: ReturnType<typeof useMeasurementAlertsRecord>) =>
	loc.type === 'device' ? measurementAlerts[loc.id] : undefined

const getActiveAlerts = (protections: AnyProtection[], alerts: Record<string, AnyAlert>) =>
	protections.map((p) => alerts[p.vrn]).filter(isNotNil)

const hasMeasurementAlerts = (ma: MeasurementAlert) => (ma?.active.length ?? 0) > 0 || (ma?.inactive.length ?? 0) > 0

const hasProtections = (protections: AnyProtection[], ma: MeasurementAlert) => protections.length > 0 || hasMeasurementAlerts(ma)

const isInAlert = (activeAlerts: AnyAlert[], ma: MeasurementAlert) => activeAlerts.length > 0 || (ma?.active.length ?? 0) > 0

const getHighestSeverity = (activeAlerts: AnyAlert[]) => Math.max(...activeAlerts.map((p) => severityAsNumberMap[p.severity]))

const getHighestProtectionTypePriority = (activeAlerts: AnyAlert[], ma: MeasurementAlert): number => {
	const types = activeAlerts.map((a) => a.type as string)
	if ((ma?.active.length ?? 0) > 0) types.push('measurement')
	return types.reduce((min, t) => {
		const idx = protectionTypePriorityOrder.indexOf(t as (typeof protectionTypePriorityOrder)[number])
		const rank = idx === -1 ? protectionTypePriorityOrder.length : idx
		return rank < min ? rank : min
	}, protectionTypePriorityOrder.length as number)
}

const getHighestPriority = (activeProtections: AnyProtection[]) => {
	if (activeProtections.length === 0) return 10
	return Math.min(...activeProtections.map((p) => p.priority))
}

const compareByStatus = (aStatus: boolean, bStatus: boolean) => {
	if (aStatus && !bStatus) return -1
	if (!aStatus && bStatus) return 1
	return 0
}

export const useSortLocations = () => {
	const { data: favorites } = useFavoriteAssets()
	const { assetProtections } = useProtections()
	const measurementAlerts = useMeasurementAlertsRecord()
	const { alerts } = useAlerts()
	const favoritesSet = useMemo(() => new Set(favorites?.map((f) => f.assetVrn) ?? []), [favorites])
	return useCallback(
		(a: Location, b: Location) => {
			const aIsFavorite = isAssetLocation(a) ? favoritesSet.has(a.assetVrn) : false
			const bIsFavorite = isAssetLocation(b) ? favoritesSet.has(b.assetVrn) : false
			const favoriteComparison = compareByStatus(aIsFavorite, bIsFavorite)
			if (favoriteComparison !== 0) return favoriteComparison

			const protectionsA = getLocationProtections(a, assetProtections)
			const protectionsB = getLocationProtections(b, assetProtections)
			const measurementAlertsA = getLocationMeasurementAlerts(a, measurementAlerts)
			const measurementAlertsB = getLocationMeasurementAlerts(b, measurementAlerts)
			const activeAlertsA = getActiveAlerts(protectionsA, alerts)
			const activeAlertsB = getActiveAlerts(protectionsB, alerts)

			// 1. Active first
			const aInAlert = isInAlert(activeAlertsA, measurementAlertsA)
			const bInAlert = isInAlert(activeAlertsB, measurementAlertsB)
			const alertComparison = compareByStatus(aInAlert, bInAlert)
			if (alertComparison !== 0) return alertComparison

			// 2. Protection type [lightning, weatherHazard, measurement, hail, wind]
			const typePriorityDiff =
				getHighestProtectionTypePriority(activeAlertsA, measurementAlertsA) -
				getHighestProtectionTypePriority(activeAlertsB, measurementAlertsB)
			if (typePriorityDiff !== 0) return typePriorityDiff

			// . Higher severity first (among active)
			if (aInAlert && bInAlert) {
				const severityDiff = getHighestSeverity(activeAlertsB) - getHighestSeverity(activeAlertsA)
				if (severityDiff !== 0) return severityDiff
			}

			// 4. Protection priority (lower number = higher priority; keeps LTZ above realtime warning lightning)
			const priorityDiff = getHighestPriority(protectionsA) - getHighestPriority(protectionsB)
			if (priorityDiff !== 0) return priorityDiff

			// 5. Has protections
			const aHasProtections = hasProtections(protectionsA, measurementAlertsA)
			const bHasProtections = hasProtections(protectionsB, measurementAlertsB)
			const protectionComparison = compareByStatus(aHasProtections, bHasProtections)
			if (protectionComparison !== 0) return protectionComparison

			// 6. Alphabetical
			return a.name.localeCompare(b.name)
		},
		[alerts, assetProtections, measurementAlerts, favoritesSet],
	)
}

export const roundCoord = (coord: number) => Number(coord.toFixed(5))
export const coordsToLonLat = (coords: Position) => ({ longitude: roundCoord(coords[0] ?? 0), latitude: roundCoord(coords[1] ?? 0) })
